<template>
  <div
    class="base-inline-status"
    :class="`base-inline-status--${kind}`"
    aria-live="polite"
  >
    <span
      v-if="kind === 'loading'"
      class="base-inline-status__spinner"
      aria-hidden="true"
    ></span>

    <span
      v-else-if="kind === 'pending'"
      class="base-inline-status__dots"
      aria-hidden="true"
    >
      <span></span>
      <span></span>
      <span></span>
    </span>

    <span
      v-else
      class="base-inline-status__icon"
      aria-hidden="true"
    >
      <AppIcon
        v-if="iconName"
        :name="iconName"
        :size="14"
      />
      <span v-else>{{ fallbackIcon }}</span>
    </span>

    <span class="base-inline-status__text">
      {{ text }}
    </span>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

const ICON_NAMES = {
  info: "info",
};

const FALLBACK_ICONS = {
  success: "✓",
  warning: "!",
  danger: "!",
  error: "!",
  empty: "i",
};

export default {
  name: "BaseInlineStatus",

  components: {
    AppIcon,
  },

  props: {
    kind: {
      type: String,
      default: "info",
      validator: (value) =>
        [
          "info",
          "success",
          "warning",
          "danger",
          "error",
          "loading",
          "pending",
          "empty",
        ].includes(value),
    },
    text: {
      type: String,
      required: true,
    },
  },

  computed: {
    iconName() {
      return ICON_NAMES[this.kind] || null;
    },

    fallbackIcon() {
      return FALLBACK_ICONS[this.kind] || "i";
    },
  },
};
</script>

<style scoped>
.base-inline-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
}

.base-inline-status--info,
.base-inline-status--empty {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.base-inline-status--pending,
.base-inline-status--warning {
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.base-inline-status--loading,
.base-inline-status--success {
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.base-inline-status--danger,
.base-inline-status--error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.base-inline-status__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 1px solid currentColor;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.base-inline-status__dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  height: 18px;
}

.base-inline-status__dots span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: inline-status-dot 1.1s ease-in-out infinite;
}

.base-inline-status__dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.base-inline-status__dots span:nth-child(3) {
  animation-delay: 0.3s;
}

.base-inline-status__spinner {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 2px solid #bbf7d0;
  border-top-color: var(--color-green);
  border-radius: 50%;
  animation: inline-status-spin 0.8s linear infinite;
}

.base-inline-status__text {
  min-width: 0;
}

@keyframes inline-status-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes inline-status-dot {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

@media (max-width: 640px) {
  .base-inline-status {
    width: 100%;
    justify-content: center;
  }
}
</style>
